import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Star, Trash2 } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { EpkContentTab } from '@/components/EpkContentTab'
import { cn } from '@/lib/utils'

interface EpkTemplate {
  id: string
  name: string
  brevoTemplateId: number
  description: string | null
  isDefault: boolean
  createdAt: string
}

interface ArtistEventType {
  id: string
  value: string
  displayName: string
  epkTemplateId: string | null
  firstSeenAt: string
  lastSeenAt: string
  submissionCount: number
  epkTemplate: EpkTemplate | null
}

interface EpkDispatchRow {
  id: string
  sentTo: string
  template: string
  sentAt: string
  brevoMessageId: string | null
  epkTemplateId: string | null
  epkTemplate: { name: string } | null
}

export function EpkSettingsPage() {
  const qc = useQueryClient()
  const [addingTemplate, setAddingTemplate] = useState(false)
  const [tab, setTab] = useState<'content' | 'dispatch'>('content')

  const templatesQ = useQuery<EpkTemplate[], ApiClientError>({
    queryKey: ['epk-templates'],
    queryFn: () => artistFetch<EpkTemplate[]>('/epks/templates'),
  })
  const eventTypesQ = useQuery<ArtistEventType[], ApiClientError>({
    queryKey: ['epk-event-types'],
    queryFn: () => artistFetch<ArtistEventType[]>('/epks/event-types'),
  })
  const dispatchesQ = useQuery<EpkDispatchRow[], ApiClientError>({
    queryKey: ['epk-dispatches'],
    queryFn: () => artistFetch<EpkDispatchRow[]>('/epks/dispatches?limit=50'),
  })

  async function deleteTemplate(id: string) {
    if (!confirm('Delete this EPK template? Event types using it will fall back to the default.')) return
    try {
      await artistFetch(`/epks/templates/${id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['epk-templates'] })
      qc.invalidateQueries({ queryKey: ['epk-event-types'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Delete failed')
    }
  }

  async function setDefault(id: string) {
    try {
      await artistFetch(`/epks/templates/${id}`, {
        method: 'PATCH',
        body: { isDefault: true },
      })
      qc.invalidateQueries({ queryKey: ['epk-templates'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Update failed')
    }
  }

  async function setEventTypeTemplate(eventTypeId: string, templateId: string | null) {
    try {
      await artistFetch(`/epks/event-types/${eventTypeId}`, {
        method: 'PATCH',
        body: { epkTemplateId: templateId },
      })
      qc.invalidateQueries({ queryKey: ['epk-event-types'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Update failed')
    }
  }

  return (
    <div>
      <PageHeader
        title="EPK settings"
        subtitle="Authorised EPK copy gates auto-dispatch; Brevo templates control email delivery."
        actions={
          tab === 'dispatch' ? (
            <button
              type="button"
              onClick={() => setAddingTemplate((x) => !x)}
              className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
            >
              <Plus className="h-4 w-4" />
              {addingTemplate ? 'Cancel' : 'New template'}
            </button>
          ) : undefined
        }
      />

      <div className="mb-6 flex gap-1 rounded-lg border border-ovation-ink/10 bg-white p-1 w-fit">
        {(['content', 'dispatch'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'rounded-md px-4 py-1.5 text-sm font-medium capitalize',
              tab === t ? 'bg-ovation-ink text-ovation-paper' : 'text-ovation-muted hover:text-ovation-ink',
            )}
          >
            {t === 'content' ? 'Content drafts' : 'Dispatch templates'}
          </button>
        ))}
      </div>

      <ChannelEpkHints />

      {tab === 'content' && <EpkContentTab />}

      {tab === 'dispatch' && (
        <>
      {addingTemplate && (
        <CreateTemplateForm
          onCreated={() => {
            setAddingTemplate(false)
            qc.invalidateQueries({ queryKey: ['epk-templates'] })
          }}
        />
      )}

      <h3 className="mb-2 mt-2 font-display text-base">Templates</h3>
      <AsyncBoundary isLoading={templatesQ.isLoading} error={templatesQ.error}>
        <DataTable
          rows={templatesQ.data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No EPK templates yet. Create one to get started — its Brevo template ID is the numeric id in the Brevo dashboard URL."
          columns={[
            {
              key: 'name',
              header: 'Name',
              render: (r) => (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{r.name}</span>
                  {r.isDefault && (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-900">
                      <Star className="h-3 w-3" /> Default
                    </span>
                  )}
                </div>
              ),
            },
            { key: 'brevo', header: 'Brevo template ID', render: (r) => r.brevoTemplateId },
            {
              key: 'description',
              header: 'Notes',
              render: (r) => (
                <span className="text-xs text-ovation-muted">{r.description ?? '—'}</span>
              ),
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              render: (r) => (
                <div className="flex items-center justify-end gap-2">
                  {!r.isDefault && (
                    <button
                      type="button"
                      onClick={() => setDefault(r.id)}
                      className="text-xs text-ovation-muted hover:text-ovation-ink"
                      title="Set as default"
                    >
                      Make default
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteTemplate(r.id)}
                    className="text-xs text-red-600 hover:text-red-700"
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ),
            },
          ]}
        />
      </AsyncBoundary>

      <h3 className="mb-2 mt-8 font-display text-base">Event types seen</h3>
      <p className="mb-3 text-xs text-ovation-muted">
        Auto-discovered from form submissions. Assign each one to a template — unassigned types fall back to the default.
      </p>
      <AsyncBoundary isLoading={eventTypesQ.isLoading} error={eventTypesQ.error}>
        <DataTable
          rows={eventTypesQ.data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No event types yet — the first form submission will populate this list."
          columns={[
            { key: 'name', header: 'Type', render: (r) => r.displayName },
            { key: 'value', header: 'Raw value', render: (r) => <code className="text-xs">{r.value}</code> },
            { key: 'count', header: 'Submissions', align: 'right', render: (r) => r.submissionCount },
            {
              key: 'lastSeen',
              header: 'Last seen',
              render: (r) => new Date(r.lastSeenAt).toLocaleDateString(),
            },
            {
              key: 'template',
              header: 'EPK template',
              render: (r) => (
                <select
                  value={r.epkTemplateId ?? ''}
                  onChange={(e) => setEventTypeTemplate(r.id, e.target.value || null)}
                  className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
                >
                  <option value="">— use default —</option>
                  {(templatesQ.data ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              ),
            },
          ]}
        />
      </AsyncBoundary>

      <h3 className="mb-2 mt-8 font-display text-base">Recent dispatches</h3>
      <AsyncBoundary isLoading={dispatchesQ.isLoading} error={dispatchesQ.error}>
        <DataTable
          rows={dispatchesQ.data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No EPKs dispatched yet."
          columns={[
            { key: 'sentAt', header: 'Sent', render: (r) => new Date(r.sentAt).toLocaleString() },
            { key: 'to', header: 'To', render: (r) => r.sentTo },
            {
              key: 'template',
              header: 'Template',
              render: (r) => r.epkTemplate?.name ?? r.template,
            },
            {
              key: 'brevo',
              header: 'Status',
              render: (r) =>
                r.brevoMessageId ? (
                  <span className="text-xs text-emerald-700">Sent</span>
                ) : (
                  <span className="text-xs text-amber-700">Skipped (no Brevo)</span>
                ),
            },
          ]}
        />
      </AsyncBoundary>
        </>
      )}
    </div>
  )
}

function CreateTemplateForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('')
  const [brevoTemplateId, setBrevoTemplateId] = useState('')
  const [description, setDescription] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setErr(null)
    try {
      await artistFetch('/epks/templates', {
        method: 'POST',
        body: {
          name: name.trim(),
          brevoTemplateId: Number(brevoTemplateId),
          description: description.trim() || undefined,
          isDefault,
        },
      })
      onCreated()
    } catch (e2) {
      setErr(e2 instanceof ApiClientError ? e2.message : 'Failed to create')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-ovation-ink/10 bg-white p-4 sm:grid-cols-2"
    >
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Name *</label>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Standard Concert EPK"
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Brevo template ID *</label>
        <input
          required
          type="number"
          value={brevoTemplateId}
          onChange={(e) => setBrevoTemplateId(e.target.value)}
          placeholder="e.g. 142"
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        />
      </div>
      <div className="sm:col-span-2">
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Notes</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="When to use this template"
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        />
      </div>
      <label className="flex items-center gap-2 sm:col-span-2">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        <span className="text-sm">Use as default when no event type matches</span>
      </label>
      {err && <div className="text-sm text-red-700 sm:col-span-2">{err}</div>}
      <button
        type="submit"
        disabled={submitting}
        className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50 sm:col-span-2"
      >
        {submitting ? 'Creating…' : 'Create template'}
      </button>
    </form>
  )
}

function ChannelEpkHints() {
  const { data } = useQuery<
    Array<{
      marketChannel: string
      recommendedEpkVariant: string
      opportunityCount: number
    }>,
    ApiClientError
  >({
    queryKey: ['booking-channels'],
    queryFn: () => artistFetch('/booking-intelligence/channels'),
  })

  if (!data?.length) return null

  return (
    <div className="mb-6 rounded-lg border border-ovation-ink/10 bg-white p-4">
      <h3 className="text-sm font-semibold text-ovation-ink">Market channel → EPK variant</h3>
      <p className="mt-1 text-xs text-ovation-muted">
        Recommended draft variant per booking channel (additive — existing variants unchanged).
      </p>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((row) => (
          <li
            key={row.marketChannel}
            className="rounded bg-ovation-paper/60 px-3 py-2 text-xs text-ovation-ink"
          >
            <span className="font-medium capitalize">
              {row.marketChannel.replace(/_/g, ' ')}
            </span>
            <span className="text-ovation-muted"> → </span>
            <span className="capitalize">{row.recommendedEpkVariant.replace(/_/g, ' ')}</span>
            {row.opportunityCount > 0 && (
              <span className="text-ovation-muted"> · {row.opportunityCount} opps</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

